import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFirestore, collection, getDocs, query, orderBy } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './StorePage.css';

const db = getFirestore();

const OrderHistoryPage = () => {
  const navigate = useNavigate();
  const { user, userCollection, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return; // wait for auth state to hydrate
    if (!user) {
      navigate('/');
      return;
    }
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, userCollection, user.uid, 'purchases'),
        orderBy('orderDate', 'desc')
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          orderDate: data.orderDate?.toDate ? data.orderDate.toDate() : new Date(data.orderDate),
        };
      });
      setOrders(list);
    } catch (e) {
      console.error('Error loading orders:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    const num = typeof val === 'number' ? val : parseFloat(val) || 0;
    return `$${num.toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const map = {
      open: { label: 'Open', color: '#856404', bg: '#fff3cd' },
      pending: { label: 'Pending', color: '#856404', bg: '#fff3cd' },
      sent_to_kitchen: { label: 'Sent to Kitchen', color: '#004085', bg: '#cce5ff' },
      in_preparation: { label: 'In Preparation', color: '#0c5460', bg: '#d1ecf1' },
      ready: { label: 'Ready', color: '#155724', bg: '#d4edda' },
      served: { label: 'Served', color: '#fff', bg: '#20c997' },
      out_for_delivery: { label: 'Out for Delivery', color: '#fff', bg: '#17a2b8' },
      complete: { label: 'Complete', color: '#fff', bg: '#343a40' },
      canceled: { label: 'Canceled', color: '#fff', bg: '#dc3545' },
    };
    const meta = map[status] || map.pending;
    return (
      <span
        style={{
          background: meta.bg,
          color: meta.color,
          padding: '4px 10px',
          borderRadius: 12,
          fontSize: '0.75rem',
          fontWeight: 600,
        }}
      >
        {meta.label}
      </span>
    );
  };

  return (
    <div className="ff-store-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <div style={{ flex: 1, padding: '24px 0' }}>
        <div className="container">
          <h3 className="font-weight-bold mb-4">
            <i className="fas fa-receipt text-primary mr-2"></i>
            My Orders
          </h3>

          {loading ? (
            <div className="ff-loading-stores">
              <div className="ff-spinner"></div>
              <p>Loading your orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-receipt fa-4x text-muted mb-4"></i>
              <h4 className="text-muted">No orders yet</h4>
              <p className="text-muted">Once you place an order, it will appear here.</p>
              <button className="btn btn-primary mt-3" onClick={() => navigate('/explore')}>
                <i className="fas fa-search mr-2"></i>Explore Stores
              </button>
            </div>
          ) : (
            <div className="row">
              {orders.map((order) => (
                <div className="col-lg-6 mb-3" key={order.id}>
                  <div
                    className="card cursor-pointer"
                    onClick={() => navigate(`/order/${order.id}`)}
                    style={{ borderRadius: 16, border: '1px solid #eee' }}
                  >
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div className="font-weight-bold" style={{ fontSize: '1.1rem' }}>
                            {order.storeName || 'Store'}
                          </div>
                          <div className="text-muted small">
                            {order.orderDate?.toLocaleDateString?.() || '—'} · #{order.confirmationNumber || order.id.slice(-6).toUpperCase()}{order.guestNumber ? ` · Guest #${order.guestNumber}` : ''}
                          </div>
                        </div>
                        <div>{getStatusBadge(order.fulfillmentStatus || order.status)}</div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted small">
                          {order.items?.length || 0} item{order.items?.length === 1 ? '' : 's'} · {order.orderType === 'delivery' ? 'Delivery' : 'Pick Up'}
                        </div>
                        <div className="font-weight-bold" style={{ color: '#ff6b35', fontSize: '1.1rem' }}>
                          {formatCurrency(order.total)}
                        </div>
                      </div>
                      {/* Review CTA for completed/open orders — hide if fully reviewed */}
                      {(() => {
                        const isEligible =
                          order.fulfillmentStatus === 'complete' ||
                          order.fulfillmentStatus === 'completed' ||
                          order.status === 'complete' ||
                          order.status === 'completed' ||
                          order.status === 'open';
                        const allItemsReviewed = (order.items || []).every((item) =>
                          order.reviewedProducts?.includes(item.productId)
                        );
                        const fullyReviewed = order.reviewedStore && allItemsReviewed;
                        return isEligible && !fullyReviewed;
                      })() && (
                        <div className="mt-3 pt-2" style={{ borderTop: '1px solid #f5f5f5' }}>
                          <button
                            className="btn btn-sm btn-block"
                            style={{
                              background: '#fff3e0',
                              color: '#ff5722',
                              border: '1px solid #ffe0b2',
                              borderRadius: 8,
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              padding: '6px 12px',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/order/${order.id}`);
                            }}
                          >
                            <i className="fas fa-star mr-2" style={{ color: '#ffc107' }}></i>
                            Rate your experience
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrderHistoryPage;
